import React, { useLayoutEffect, useEffect, useCallback, useState } from "react";
import { Layout, ConfigProvider, theme } from "antd";
import { useLocation, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Header } from "./Component/header";
import { Login } from "./Pages/Login";
import { RouterWrapper } from "./router/routerWrapper";
import { PermissionSideMenu } from "./Component/PermissionSideMenu";
import ReminderModal from "./Component/ReminderModal/ReminderModal";
import { CompanySetupRequiredModal } from "./Component/CompanySetupRequiredModal";
import { OrderMonthlyLimitReachedModal } from "./Component/OrderMonthlyLimitReachedModal";
import { SocketContextProvider } from "./Socket";
import { NotificationsListener } from "./Socket/NotificationsListener";
import { ImportEventsListener } from "./Socket/ImportEventsListener";
import { NotificationsProvider } from "./Socket/notificationsStore";
import { ConnectionAlert } from "./Component/ConnectionAlert/ConnectionAlert";
import { PermissionLoader } from "./Component/PermissionLoader";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "./Component/ErrorFallback/ErrorFallback";
import { useTheme } from "./Hooks/useTheme";
import {
  themeTokens,
  brandTokens,
  assertTokensInSync,
} from "./styles/tokens";
import useTokenRefresh from "./Hooks/useTokenRefresh";
import Api from "./Network";
import { setUserInfo, setCompany } from "./redux/action";
import { message } from "antd";
import { ResponsiveProvider } from "./Hooks/useContext";
import './i18n';
import "./styles/index.scss";
import "./styles/_reset.scss";
import { VoipDialerListener } from './Socket/VoipDialerListener';
import { VoipWidget } from './Component/VOIP/VoipWidget';
import { primeAudioUnlock } from './Utilis/playNotificationSound';
function App() {
  const { Content } = Layout;
  const token = localStorage.getItem("token");
  const history = useHistory();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state: any) => state.setReducer);
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  
  // Proactively refresh token before it expires
  useTokenRefresh();

  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    primeAudioUnlock();
  }, []);
  

  
  const handleLoadUserData = useCallback(async () => {
    setIsLoadingUserData(true);
    try {
      const userResponse = await Api.post("auth/me",{});
      dispatch(setUserInfo(userResponse));

      const companyResponse = await Api.get("company");
      dispatch(setCompany(companyResponse));
    } catch (error: any) {
      // Check if this is a network error (no response object)
      const isNetworkError = !error?.response && (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error');
      
      // Validation errors are already handled by the Network interceptor
      const responseData = error?.response?.data;
      const hasValidationErrors = responseData?.errors || responseData?.status === "error";
      const status = error?.response?.status;
      
      // Check production mode
      const nodeEnv = import.meta.env.MODE;
      const reactAppEnv = import.meta.env.VITE_ENV;
      const isProduction = nodeEnv === 'production' || 
                         reactAppEnv === 'production' || 
                         (nodeEnv !== 'development' && nodeEnv !== 'test' && nodeEnv !== undefined);
      
      // Don't show errors for:
      // 1. Network errors (CORS, connection issues) - these are usually backend/server issues
      // 2. Validation errors (already shown by Network interceptor)
      // 3. Production mode
      // Only show generic errors in development/staging for non-network, non-validation errors
      if (!isNetworkError && !hasValidationErrors && !isProduction) {
        // Only show for actual API errors with response (not network errors)
        if (error?.response) {
          const errorMsg = responseData?.message || "failed to load user data";
          // Show status code and error message in development/staging
          message.error(`${errorMsg} (${status})`);
        }
      }
      // Silently fail for network errors, validation errors, and in production
    } finally {
      setIsLoadingUserData(false);
    }
  }, []);

  useLayoutEffect(() => {
    // If there's no token but userInfo exists, clear the userInfo
    if (!token && userInfo) {
      dispatch(setUserInfo(null));
      return;
    }
    
    // If there's a token and no userInfo, load user data
    if (token && !userInfo) {
    handleLoadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Removed userInfo from dependencies

  const ScrollToTop = () => {
    useEffect(() => {
      const unListen = history.listen(() => {
        window.scrollTo(0, 0);
      });

      return () => {
        unListen();
      };
    }, [history]);

    return null;
  };
  const { theme: currentTheme, isDark } = useTheme();
  const palette = themeTokens[currentTheme];

  // Dev-only: warn if styles/tokens.ts has drifted from styles/index.scss.
  useEffect(() => {
    assertTokensInSync(currentTheme);
  }, [currentTheme]);
  return (
    <ResponsiveProvider>
      <div className={`App ${isDark ? "dark-theme" : "light-theme"}`}>
        {token ? (
          <SocketContextProvider>
            <ConfigProvider
              theme={{
                // Array form: compact applies in BOTH themes. Previously dark
                // mode swapped compactAlgorithm out for darkAlgorithm, so
                // switching theme also silently changed component density.
                algorithm: isDark
                  ? [theme.compactAlgorithm, theme.darkAlgorithm]
                  : [theme.compactAlgorithm],
                // Values follow the Awfar Design System; the palette comes
                // from styles/tokens.ts, which mirrors styles/index.scss.
                token: {
                  colorPrimary: palette["--ds-primary"],
                  colorLink: isDark ? palette["--ds-primary-ink"] : brandTokens.brandHover,
                  colorInfo: palette["--ds-primary"],
                  colorSuccess: palette["--success-ink"],
                  colorWarning: palette["--warn-ink"],
                  colorError: palette["--danger-ink"],
                  fontFamily: brandTokens.fontFamily,
                  borderRadius: brandTokens.radiusControl,
                  borderRadiusLG: brandTokens.radiusCard,
                  borderRadiusSM: 8,
                  colorBgLayout: palette["--bg-app"],
                  colorBgContainer: palette["--bg-surface"],
                  colorBgElevated: palette["--bg-surface-raised"],
                  colorBgMask: isDark ? "rgba(0, 0, 0, 0.62)" : "rgba(11, 36, 102, 0.45)",
                  colorFillAlter: palette["--bg-surface-2"],
                  controlItemBgActive: palette["--ds-surface-selected"],
                  // Own token: in dark, surface-2 is the popup surface itself,
                  // so hovered dropdown/menu rows showed no highlight
                  controlItemBgHover: palette["--ds-option-hover"],
                  colorText: palette["--text-primary"],
                  colorTextSecondary: palette["--text-secondary"],
                  colorTextTertiary: palette["--text-tertiary"],
                  colorTextPlaceholder: palette["--ds-ink-placeholder"],
                  colorBorder: palette["--ds-field-border"],
                  colorBorderSecondary: palette["--border-subtle"],
                  colorSplit: palette["--border-subtle"],
                  colorPrimaryHover: palette["--ds-field-border-focus"],
                  controlOutline: isDark ? "rgba(77, 166, 255, 0.24)" : "rgba(26, 127, 253, 0.16)",
                  boxShadow: isDark
                    ? "0 6px 16px rgba(0, 0, 0, 0.45)"
                    : "0 6px 16px rgba(26, 127, 253, 0.16)",
                },
                components: {
                  // Global modal breathing room: the compact algorithm shrinks
                  // antd's default modal padding, leaving content stuck to the edges.
                  // `contentPadding` is applied at runtime but is not part of antd's
                  // public Modal token typings, hence the cast.
                  Modal: {
                    contentPadding: "24px 28px",
                  } as any,
                  // ds Button: pill, 40px, 13px/500 (primary and quiet)
                  Button: {
                    fontWeight: 500,
                    controlHeight: 40,
                    controlHeightSM: 30,
                    controlHeightLG: 46,
                    paddingInline: 18,
                    contentFontSize: 13,
                    borderRadius: 999,
                    borderRadiusLG: 999,
                    borderRadiusSM: 8,
                    primaryShadow: isDark
                      ? "0 8px 18px rgba(7, 46, 99, 0.55)"
                      : "0 8px 18px rgba(26, 127, 253, 0.35)",
                  },
                  // ds fields: white, 42px, radius-xl, field-border
                  Input: {
                    controlHeight: brandTokens.fieldHeight,
                    borderRadius: brandTokens.radiusField,
                    hoverBorderColor: palette["--ds-field-border-focus"],
                    activeBorderColor: palette["--ds-field-border-focus"],
                    fontSize: 13.5,
                  },
                  InputNumber: {
                    controlHeight: brandTokens.fieldHeight,
                    borderRadius: brandTokens.radiusField,
                    hoverBorderColor: palette["--ds-field-border-focus"],
                    activeBorderColor: palette["--ds-field-border-focus"],
                  },
                  Select: {
                    controlHeight: brandTokens.fieldHeight,
                    borderRadius: brandTokens.radiusField,
                    hoverBorderColor: palette["--ds-field-border-focus"],
                    activeBorderColor: palette["--ds-field-border-focus"],
                    optionSelectedBg: palette["--ds-surface-selected"],
                    optionSelectedColor: palette["--ds-primary-ink"],
                    optionActiveBg: palette["--ds-option-hover"],
                  },
                  DatePicker: {
                    controlHeight: brandTokens.fieldHeight,
                    borderRadius: brandTokens.radiusField,
                    hoverBorderColor: palette["--ds-field-border-focus"],
                    activeBorderColor: palette["--ds-field-border-focus"],
                  },
                  Table: {
                    headerBg: palette["--bg-surface-2"],
                    headerColor: palette["--text-secondary"],
                    rowHoverBg: palette["--ds-surface-selected"],
                    rowSelectedBg: palette["--ds-surface-selected"],
                    borderColor: palette["--ds-line-table"],
                  },
                  Tabs: {
                    itemColor: palette["--text-secondary"],
                  },
                  Menu: {
                    itemBg: "transparent",
                    subMenuItemBg: "transparent",
                    itemSelectedBg: palette["--bg-tint"],
                    itemColor: palette["--text-secondary"],
                    itemBorderRadius: brandTokens.radiusControl,
                  },
                },
              }}
            >
              <NotificationsProvider>
                <NotificationsListener />
                <ImportEventsListener />
                <VoipDialerListener />
                <VoipWidget />
                {/* Inbox theme shell: the sidebar runs the full height with the
                    logo on top; the header sits inside the main column. */}
                <Layout className="app-shell">
                  <PermissionSideMenu />
                  <ReminderModal />
                  <CompanySetupRequiredModal />
                  <OrderMonthlyLimitReachedModal />
                  <Layout className="site-layout">
                    <Header />
                    <Content
                      // ds layout: 16px page gutter; the sidebar's own
                      // padding supplies the gap on its side
                      style={{ paddingBlock: "0 16px", paddingInline: "0 16px" }}
                      className="site-layout-background"
                    >
                      <PermissionLoader>
                        <ScrollToTop />
                        <RouterWrapper />
                        <ConnectionAlert />
                      </PermissionLoader>
                    </Content>
                  </Layout>
                </Layout>
              </NotificationsProvider>
            </ConfigProvider>
          </SocketContextProvider>
        ) : (
          <Login />
        )}
      </div>
    </ResponsiveProvider>
  );
}

export default App;
